import { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import { DateTimeRangePicker, Flex, Pane, Text } from '@leverege/ui-elements';
import { Toolbar } from '@leverege/ui-plugin';
import { DateTime } from 'luxon';
import TimelineUtil from "./TimelineUtil.js";
import TimelineAxis from "./TimelineAxis.js";
import useTimelineProjection from "./hooks/useTimelineProjection.js";
import S from './TimelineScrubber.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default function TimelineScrubber(props) {
  const {
    addDataset,
    // For later use with timeline plugins
    clientProps,
    isPlaying,
    loading,
    onJumpToNow,
    onChangeRange,
    onChangeScrubTime,
    onPlayPause,
    range,
    scrubTime,
    ticks
  } = props;
  const {
    height: stageHeight,
    projection,
    ref: timelineStageRef
  } = useTimelineProjection({
    range
  });
  const [seekHeadLeft, setSeekHeadLeft] = useState(null);
  const [seekTime, setSeekTime] = useState(null);
  const playheadLeft = useMemo(() => {
    if (!scrubTime) {
      return 0;
    }
    const time = TimelineUtil.coerceDate(scrubTime).valueOf();
    return projection.toScreen(time);
  }, [projection, scrubTime]);
  const {
    formattedStartTime,
    formattedEndTime
  } = useMemo(() => {
    if (scrubTime) {
      const startDateTime = TimelineUtil.coerceDateTime(range.start);
      const endDateTime = TimelineUtil.coerceDateTime(range.end);
      return {
        formattedStartTime: startDateTime.toLocaleString(DateTime.DATETIME_SHORT),
        formattedEndTime: endDateTime.toLocaleString(DateTime.DATETIME_SHORT)
      };
    }
    return {};
  }, [scrubTime, range]);
  const onPlayheadParentMouseMove = useCallback(evt => {
    const x = Math.min(evt.currentTarget.offsetWidth, Math.max(0, evt.clientX - evt.currentTarget.getBoundingClientRect().left));
    const seekDate = TimelineUtil.coerceDate(projection.toValue(x));
    const start = TimelineUtil.coerceDate(range?.start);
    const end = TimelineUtil.coerceDate(range?.end);
    if (!evt.shiftKey) {
      const searchable = [start, ...ticks.map(tick => tick.date.toJSDate()), end];
      const nearest = TimelineUtil.binaryTimeSearch(seekDate, searchable);
      if (nearest) {
        setSeekTime(TimelineUtil.coerceDateTime(nearest));
        setSeekHeadLeft(projection.toScreen(nearest.getTime()));
        return;
      }
    }
    setSeekTime(TimelineUtil.coerceDateTime(seekDate));
    setSeekHeadLeft(projection.toScreen(seekDate.valueOf()));
  }, [range, ticks, projection]);
  const onPlayheadParentMouseLeave = useCallback(() => {
    setSeekTime(null);
    setSeekHeadLeft(null);
  }, []);
  const onPlayheadParentClick = useCallback(() => {
    if (loading || isPlaying) {
      return;
    }
    const time = projection.toValue(seekHeadLeft);
    onChangeScrubTime(time);
  }, [seekHeadLeft, onChangeScrubTime, loading, projection, isPlaying]);
  const actionBarMatchContext = useMemo(() => {
    return {
      client: 'TimelineScrubber',
      path: clientProps.path,
      objectType: clientProps.objectType,
      use: 'actionBar'
    };
  }, [clientProps.path, clientProps.objectType]);
  const playBarMatchContext = useMemo(() => {
    return {
      client: 'TimelineScrubber',
      path: clientProps.path,
      objectType: clientProps.objectType,
      use: 'playBar'
    };
  }, [clientProps.path, clientProps.objectType]);
  const context = useMemo(() => {
    return {
      clientProps,
      isPlaying,
      loading,
      onChangeRange,
      onJumpToNow,
      onPlayPause,
      range,
      scrubTime
    };
  }, [clientProps, isPlaying, loading, onChangeRange, onJumpToNow, onPlayPause, range, scrubTime]);
  return /*#__PURE__*/_jsxs(Pane, {
    variant: "timelineScrubberPlayBar",
    layout: "flex:col",
    children: [/*#__PURE__*/_jsxs(Pane, {
      variant: "timelineScrubberTimeline",
      layout: "flex:rowMCenter",
      children: [/*#__PURE__*/_jsx(Text, {
        className: S.timelineScrubberLabel,
        variant: "bodySmallBold",
        children: "Timeline"
      }), /*#__PURE__*/_jsxs(Pane, {
        className: S.timelineFlex,
        layout: "flex:colS",
        children: [/*#__PURE__*/_jsxs(Flex, {
          variant: "rowSpaceBetween",
          children: [/*#__PURE__*/_jsx(Text, {
            variant: "captionSmall",
            children: formattedStartTime
          }), /*#__PURE__*/_jsx(Text, {
            variant: "captionSmallQuiet|captionSmall",
            children: seekTime?.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
          }), /*#__PURE__*/_jsx(Text, {
            variant: "captionSmall",
            children: formattedEndTime
          })]
        }), /*#__PURE__*/_jsxs("div", {
          ref: timelineStageRef,
          onMouseMove: onPlayheadParentMouseMove,
          onMouseLeave: onPlayheadParentMouseLeave,
          onClick: onPlayheadParentClick,
          style: {
            '--playhead-left': `${playheadLeft}px`,
            '--seekhead-left': `${seekHeadLeft}px`,
            '--timeline-stage-height': `${stageHeight}px`
          },
          className: classnames(S.playheadParent, loading && S.loading),
          children: [/*#__PURE__*/_jsx("div", {
            "data-date": scrubTime.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS),
            className: classnames(S.playhead, loading && S.loading, loading && 'fa-spin')
          }), seekHeadLeft != null && /*#__PURE__*/_jsx("div", {
            className: classnames(S.seekhead, loading && S.loading, isPlaying && S.playing)
          }), /*#__PURE__*/_jsx(Pane, {
            variant: "timelineScrubberProgressBar"
          }), /*#__PURE__*/_jsx(TimelineAxis, {
            projection: projection,
            range: range,
            ticks: ticks
          })]
        })]
      }), /*#__PURE__*/_jsx(Toolbar, {
        variant: "actionBar",
        prefer: "icon",
        matchContext: actionBarMatchContext,
        context: context
      })]
    }), /*#__PURE__*/_jsxs(Pane, {
      variant: "timelineScrubberControls",
      layout: "flex:rowSpaceBetween",
      children: [/*#__PURE__*/_jsx(Toolbar, {
        layoutVariant: "flex:rowMCenter",
        variant: "playBar|actionBar",
        matchContext: playBarMatchContext,
        context: context
      }), /*#__PURE__*/_jsx(DateTimeRangePicker, {
        value: range,
        onChange: onChangeRange
      })]
    })]
  });
}